/**
 * Kết quả chạy → mô hình hiển thị. Nguồn là kết quả test-run (một đoạn) hoặc
 * nhật ký run-logs (mỗi đoạn trước/sau khối Chờ là một dòng, chung runId).
 */
import { i18n } from '@/lib/i18n'
import type { FlowExecutionResult, NodeExecutionResult, RunLogRow } from '@/hooks/use-automation'
import { actionMeta, DON_VI_CHO_LABEL } from './catalog'
import type { KindKhoi, TrangThaiBuoc } from './flow-adapter'

export interface BuocChay {
  nodeId: string
  label: string
  kind: KindKhoi
  status: TrangThaiBuoc
  /** Một dòng tóm tắt tiếng Việt cho người dùng. */
  tomTat: string
  branch?: 'true' | 'false'
  output?: Record<string, unknown>
  error?: string
  durationMs: number
}

export type KetQuaChay = 'dang_cho' | 'thanh_cong' | 'mot_phan' | 'that_bai' | 'bo_qua'

export const KET_QUA_LABEL: Record<KetQuaChay, { ten: string; variant: 'success' | 'destructive' | 'warning' | 'secondary' | 'default' }> = {
  dang_cho: { get ten() { return i18n.t('autoCore:run.result.dang_cho') }, variant: 'default' },
  thanh_cong: { get ten() { return i18n.t('autoCore:run.result.thanh_cong') }, variant: 'success' },
  mot_phan: { get ten() { return i18n.t('autoCore:run.result.mot_phan') }, variant: 'warning' },
  that_bai: { get ten() { return i18n.t('autoCore:run.result.that_bai') }, variant: 'destructive' },
  bo_qua: { get ten() { return i18n.t('autoCore:run.result.bo_qua') }, variant: 'secondary' },
}

export interface LanChay {
  runId: string
  ruleId: string
  ruleName: string
  startedAt: string
  isTest: boolean
  ketQua: KetQuaChay
  /** Từ lúc bắt đầu tới hết đoạn cuối (gồm cả thời gian nằm chờ). */
  thoiLuongMs: number
  soDoan: number
  khach: string | null
  steps: BuocChay[]
  context: Record<string, unknown> | null
  /** Có khi sự kiện không thoả lọc "Chỉ khi" (kết quả bo_qua). */
  lyDoBoQua?: string
}

/** Thông tin để đoán loại khối khi không có sẵn flow (nhật ký chung). */
function doanKind(r: NodeExecutionResult, kinds?: Map<string, KindKhoi>): KindKhoi {
  const k = kinds?.get(r.nodeId)
  if (k) return k
  if (r.branchTaken) return 'condition'
  if (r.output && ('resumeAt' in r.output || 'delayMs' in r.output)) return 'delay'
  return 'action'
}

const fmtNgayGio = (iso: string) => new Date(iso).toLocaleString(i18n.language || 'vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })

/** Mô tả ngắn output của một action (chạy thật hoặc mô phỏng). */
export function tomTatOutput(o: Record<string, unknown> | undefined, actionType?: string): string {
  if (!o) return i18n.t('autoCore:run.done')
  if (o.skipped === true) return i18n.t('autoCore:run.skipped', { reason: String(o.reason ?? '') })
  const sim = o.simulated === true
  const lam = (viec: string, vars?: Record<string, unknown>) => i18n.t(`autoCore:run.out.${sim ? 'sim' : 'done'}.${viec}`, vars)
  const a = String(o.action ?? actionType ?? '')
  if (a === 'http_request') return lam('http', { method: String(o.method), url: String(o.url), status: String(o.status) })
  if (a === 'send_friend_request') return lam('friendRequest', { phone: String(o.phone ?? ''), campaign: String(o.campaign ?? '') })
  if (a === 'add_to_broadcast_campaign') return lam('broadcast', { campaign: String(o.campaign ?? '') })
  if (a === 'send_notification' && Array.isArray(o.recipients)) return lam('notify', { recipients: o.recipients.join(', '), title: String(o.title ?? '') })
  if (typeof o.text === 'string') return lam('text', { text: o.text })
  if (a === 'send_zalo_zns' && o.phone) return lam('zns', { templateId: String(o.templateId ?? ''), phone: String(o.phone) })
  if (a === 'add_tag') return lam('addTag', { tag: String(o.tag) })
  if (a === 'remove_tag') return lam('removeTag', { tag: String(o.tag) })
  if (o.stage) return lam('stage', { stage: String(o.stage) })
  if (a === 'increment_property') return lam(o.value !== undefined && !sim ? 'incrementValue' : 'increment', { amount: String(o.amount), field: String(o.fieldKey), value: String(o.value) })
  if (o.fieldKey) return lam('setProp', { field: String(o.fieldKey), value: String(o.value ?? '') })
  if (o.eventName) return lam('event', { name: String(o.eventName) })
  if (o.userName || o.userId) return lam('assign', { name: String(o.userName ?? o.userId) })
  if (o.scheduledAt) return lam('appointment', { time: fmtNgayGio(String(o.scheduledAt)) })
  if (typeof o.note === 'string') return o.note
  return sim ? i18n.t('autoCore:run.out.sim.generic', { name: actionMeta(a).ten }) : i18n.t('autoCore:run.done')
}

export function buocTuKetQua(r: NodeExecutionResult, kinds?: Map<string, KindKhoi>): BuocChay {
  const kind = doanKind(r, kinds)
  let status: TrangThaiBuoc = r.status === 'success' ? 'ok' : r.status === 'error' ? 'fail' : 'skip'
  let tomTat: string
  if (r.status === 'error') tomTat = i18n.t('autoCore:run.step.error', { error: r.error ?? i18n.t('autoCore:run.step.unknownError') })
  else if (r.status === 'wip') tomTat = i18n.t('autoCore:run.step.wip')
  else if (kind === 'condition') tomTat = i18n.t('autoCore:run.step.branch', { branch: i18n.t(r.branchTaken === 'false' ? 'autoCore:run.step.branchFalse' : 'autoCore:run.step.branchTrue') })
  else if (kind === 'delay') {
    const o = r.output ?? {}
    if (typeof o.resumeAt === 'string') { status = 'wait'; tomTat = i18n.t('autoCore:run.step.waitUntil', { time: fmtNgayGio(o.resumeAt) }) }
    else if (o.simulated) tomTat = i18n.t('autoCore:run.step.simDelay', { duration: moTaThoiLuong(Number(o.delayMs ?? 0)) })
    else tomTat = i18n.t('autoCore:run.step.waitDone')
  } else if (r.status === 'skipped') tomTat = r.output?.reason ? i18n.t('autoCore:run.skipped', { reason: String(r.output.reason) }) : i18n.t('autoCore:run.step.disabled')
  else tomTat = tomTatOutput(r.output)
  return {
    nodeId: r.nodeId, label: r.nodeLabel, kind, status, tomTat,
    branch: r.branchTaken, output: r.output, error: r.error, durationMs: r.durationMs,
  }
}

function ketQua(steps: BuocChay[], dangCho: boolean): KetQuaChay {
  if (dangCho) return 'dang_cho'
  const loi = steps.some((s) => s.status === 'fail')
  const thanhCong = steps.some((s) => s.kind === 'action' && s.status === 'ok')
  if (loi) return thanhCong ? 'mot_phan' : 'that_bai'
  return 'thanh_cong'
}

/** Kết quả một lần chạy thử (một đoạn) → LanChay. */
export function lanChayTuTestRun(r: FlowExecutionResult, kinds?: Map<string, KindKhoi>, khach?: string | null): LanChay {
  const steps = r.nodeResults.map((x) => buocTuKetQua(x, kinds))
  return {
    runId: r.runId, ruleId: r.ruleId, ruleName: r.ruleName, startedAt: new Date().toISOString(),
    isTest: true, ketQua: r.filtered ? 'bo_qua' : ketQua(steps, false), thoiLuongMs: r.durationMs, soDoan: 1,
    khach: khach ?? null, steps, context: null,
    ...(r.filtered ? { lyDoBoQua: r.filterReason ?? i18n.t('autoCore:run.filterDefault') } : {}),
  }
}

/** Gộp các dòng nhật ký (mỗi đoạn một dòng) thành các lần chạy, mới nhất trước. */
export function gopNhatKy(rows: RunLogRow[], kinds?: Map<string, KindKhoi>): LanChay[] {
  const theoRun = new Map<string, RunLogRow[]>()
  for (const row of rows) {
    const list = theoRun.get(row.runId) ?? []
    list.push(row)
    theoRun.set(row.runId, list)
  }
  const ra: LanChay[] = []
  for (const [runId, doan] of theoRun) {
    doan.sort((a, b) => a.segment - b.segment || a.createdAt.localeCompare(b.createdAt))
    const dau = doan[0]
    const cuoi = doan[doan.length - 1]
    // Job bị thử lại ghi thêm dòng cho CÙNG đoạn: gộp theo khối, giữ kết quả thật
    // thay vì bước "đã chạy ở lần thử trước" của lần sau.
    const theoDoan = new Map<number, Map<string, NodeExecutionResult>>()
    for (const d of doan) {
      const khoi = theoDoan.get(d.segment) ?? new Map<string, NodeExecutionResult>()
      for (const r of d.metadata?.nodeResults ?? []) {
        if (khoi.has(r.nodeId) && r.output?.daChay === true) continue
        khoi.set(r.nodeId, r)
      }
      theoDoan.set(d.segment, khoi)
    }
    const steps = [...theoDoan.values()].flatMap((khoi) => [...khoi.values()].map((x) => buocTuKetQua(x, kinds)))
    // Đoạn cuối vẫn đang hẹn khối Chờ, nhưng nếu các bước "wait" đã có đoạn sau nối tiếp thì không còn chờ.
    const dangCho = cuoi.waiting
    for (const s of steps) {
      if (s.status === 'wait' && !dangCho) { s.status = 'ok'; s.tomTat = typeof s.output?.resumeAt === 'string' ? i18n.t('autoCore:run.step.waitedUntil', { time: fmtNgayGio(s.output.resumeAt) }) : i18n.t('autoCore:run.step.waitedGeneric', { text: s.tomTat.charAt(0).toLowerCase() + s.tomTat.slice(1) }) }
    }
    const ctx = dau.metadata?.context ?? null
    const boQua = dau.metadata?.filtered === true
    // Thời điểm log được ghi = lúc đoạn KẾT THÚC → bắt đầu = createdAt − durationMs.
    const batDau = new Date(dau.createdAt).getTime() - (dau.metadata?.durationMs ?? 0)
    ra.push({
      runId, ruleId: dau.ruleId, ruleName: dau.ruleName,
      startedAt: new Date(batDau).toISOString(),
      isTest: dau.action === 'test_run',
      ketQua: boQua ? 'bo_qua' : ketQua(steps, dangCho),
      thoiLuongMs: new Date(cuoi.createdAt).getTime() - batDau,
      soDoan: theoDoan.size,
      khach: ctx?.contactName ?? ctx?.contactPhone ?? null,
      steps,
      context: ctx as Record<string, unknown> | null,
      ...(boQua ? { lyDoBoQua: dau.metadata?.filterReason ?? i18n.t('autoCore:run.filterDefault') } : {}),
    })
  }
  return ra.sort((a, b) => b.startedAt.localeCompare(a.startedAt))
}

// ── Lọc nhật ký (dùng ở hộp Nhật ký) ─────────────────────────────────
export type ThoiLuong = 'all' | 'duoi_1_phut' | 'duoi_1_gio' | 'duoi_1_ngay' | 'tren_1_ngay'
export const THOI_LUONG_LABEL: Record<ThoiLuong, string> = {
  get all() { return i18n.t('autoCore:run.duration.all') },
  get duoi_1_phut() { return i18n.t('autoCore:run.duration.duoi_1_phut') },
  get duoi_1_gio() { return i18n.t('autoCore:run.duration.duoi_1_gio') },
  get duoi_1_ngay() { return i18n.t('autoCore:run.duration.duoi_1_ngay') },
  get tren_1_ngay() { return i18n.t('autoCore:run.duration.tren_1_ngay') },
}
const PHUT = 60_000, GIO = 3_600_000, NGAY = 86_400_000

export function trongThoiLuong(ms: number, t: ThoiLuong): boolean {
  switch (t) {
    case 'duoi_1_phut': return ms < PHUT
    case 'duoi_1_gio': return ms >= PHUT && ms < GIO
    case 'duoi_1_ngay': return ms >= GIO && ms < NGAY
    case 'tren_1_ngay': return ms >= NGAY
    default: return true
  }
}

export function moTaThoiLuong(ms: number): string {
  if (!ms || ms < 1000) return i18n.t('autoCore:run.span.instant')
  if (ms < PHUT) return i18n.t('autoCore:run.span.seconds', { n: Math.round(ms / 1000) })
  if (ms < GIO) return i18n.t('autoCore:run.span.minutes', { n: Math.round(ms / PHUT) })
  if (ms < NGAY) return i18n.t('autoCore:run.span.hours', { n: +(ms / GIO).toFixed(1) })
  return i18n.t('autoCore:run.span.days', { n: +(ms / NGAY).toFixed(1) })
}

export const moTaCho = (duration: unknown, unit: unknown) =>
  `${Number(duration ?? 0)} ${DON_VI_CHO_LABEL[String(unit ?? 'seconds')] ?? String(unit)}`
